<script setup lang="ts">
// "ⓘ" toggletip for a control's description. Replaces the old
// `title`-only icon, which touch screens can't open at all: the
// description now opens on tap / click / Enter and closes on Esc, a
// second tap, or a tap elsewhere (popover light-dismiss).
//
// The bubble is a `popover="auto"` element in the top layer, so an
// accordion, the drilldown modal, or a customer card with
// `overflow: hidden` can't clip it. Toggling is declarative via
// `popovertarget`; browsers without the Popover API get the text
// inline under the label instead.

import { computed, ref, useId } from "vue";

import { useAnchoredPopover } from "./anchored-popover";

const props = defineProps<{
  text: string;
  /** The control's label, for the button's accessible name. */
  label?: string;
}>();

const bubbleId = useId();
const btn = ref<HTMLElement | null>(null);
const bubble = ref<HTMLElement | null>(null);
const { open, supported } = useAnchoredPopover(btn, bubble, { preferredHeight: 160 });

// Fallback (no Popover API): toggle an inline copy of the text.
const fallbackOpen = ref(false);
const expanded = computed(() => (supported ? open.value : fallbackOpen.value));
const name = computed(() => (props.label ? `About ${props.label}` : "More information"));

function onClick(e: MouseEvent): void {
  // Never let the tap reach an enclosing control (a label, a row).
  e.stopPropagation();
  if (!supported) fallbackOpen.value = !fallbackOpen.value;
}
</script>

<template>
  <button
    ref="btn"
    type="button"
    class="info-tip"
    :aria-label="name"
    :aria-expanded="expanded"
    :aria-controls="bubbleId"
    :popovertarget="supported ? bubbleId : undefined"
    @click="onClick"
  >
    <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true" fill="none"
      stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  </button>
  <span
    v-if="supported"
    :id="bubbleId"
    ref="bubble"
    popover="auto"
    role="tooltip"
    class="info-tip-bubble"
  >{{ text }}</span>
  <span
    v-else-if="fallbackOpen"
    :id="bubbleId"
    role="note"
    class="info-tip-inline"
  >{{ text }}</span>
</template>

<style scoped>
.info-tip {
  position: relative;
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0;
  border: 0;
  background: transparent;
  color: inherit;
  opacity: 0.65;
  cursor: pointer;
  line-height: 1;
  border-radius: 50%;
  touch-action: manipulation;
}
.info-tip:hover,
.info-tip[aria-expanded="true"] {
  opacity: 1;
}
.info-tip:focus-visible {
  outline: 2px solid var(--primary, currentColor);
  outline-offset: 2px;
}
/* Touch: a ~41px hit area without changing layout. */
@media (pointer: coarse) {
  .info-tip::after {
    content: "";
    position: absolute;
    inset: -14px;
  }
}
.info-tip-bubble {
  /* Top-layer popover: positioned by `useAnchoredPopover`. Only the
     open state sets `display`, so the UA's hidden rule still applies
     when closed. */
  position: fixed;
  inset: auto;
  margin: 0;
  box-sizing: border-box;
  max-width: min(280px, calc(100vw - 16px));
  overflow: auto;
  padding: 8px 10px;
  border: 1px solid var(--rule, #e5e7eb);
  border-radius: var(--radius, 8px);
  background: var(--card, #fff);
  color: var(--card-ink, #1a1a1a);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  font: 400 12.5px/1.45 var(--font, system-ui, sans-serif);
  text-transform: none;
  letter-spacing: normal;
  white-space: normal;
  overflow-wrap: anywhere;
}
.info-tip-inline {
  display: block;
  flex-basis: 100%;
  margin-top: 2px;
  font-size: 12px;
  line-height: 1.4;
  white-space: normal;
  overflow-wrap: anywhere;
}
</style>
